/* eslint-disable ngrx/prefer-effect-callback-in-block-statement */
import { CurrencyPipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { addDays } from 'date-fns';
import { flatMap as _flatMap, get as _get, has as _has } from 'lodash';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { env } from '@amfam/environment';
import {
  addVehicleInfoQuery,
  AddVehicleInitiateRequest,
  AddVehicleNotificationRequestAgentEmail,
  AddVehicleNotificationRequestPartnerEmail,
  addVehicleQuoteQuery,
  addVehicleInfoQuery as addVehicleStateInfoQuery,
  ANSRequest,
  fromAddVehicleBindActions,
  fromAddVehicleFeaturesActions,
  fromAddVehicleInfoActions,
  fromAddVehicleQuoteActions,
  typeOfAgentEmail,
  vehicleCAPartnerUse,
  addVehicleFeatureQuery as vehicleFeatureQuery,
  vehiclePartnerUse,
  vehiclePrimaryUse
} from '@amfam/policy/add-vehicle/data-access';
import { PolicySelectors as policyQuery, PolicySelectors } from '@amfam/policy/data-access';
import { Address, AutoPolicy, MessageDetail } from '@amfam/policy/models';
import { PrettyPolicyNum } from '@amfam/policy/pipes';
import { VehicleQuoteService } from '@amfam/policy/shared';
import {
  addVehicleCoverageForChangeCoverage,
  bindPartnerAddVehicleChangeCoverageQuoteSuccess,
  VehicleCoveragesFacade
} from '@amfam/policy/vehicle-coverages/data-access';
import { vehicleQuery } from '@amfam/policy/vehicle/data-access';
import { PaymentScheduleItem, PaymentScheduleStatusType } from '@amfam/shared/models';
import { userQuery } from '@amfam/shared/user';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService, TemplatedEmail, UtilService } from '@amfam/shared/utility/shared-services';

import * as VehicleInfoActions from '../+state/add-vehicle-feature.actions';
import { vehicleCoverageSelectionOptions, VehicleFeaturesInfoModel } from '../models';
import { Code, Content } from '../models/expectation-setting.model';
import { AddVehicleFeatureService } from '../services/add-vehicle-feature.service';
import {
  addVehicleBindPartnerQuote,
  addVehicleBindQuote,
  addVehicleBindReset,
  addVehicleExpectationSettingNotification,
  addVehicleInitiate,
  addVehicleRate,
  addVehicleReset,
  addVehicleResetRate,
  addVehicleSendAnalytics,
  addVehicleSendNotification,
  addVehicleSendNotificationAfterBind,
  addVehicleSendPartnerNotification,
  buildPartnerCoverages,
  fetchVehicleFeature,
  loadFuturePaymemts
} from './add-vehicle-feature.actions';
import { addVehicleFeatureQuery } from './add-vehicle-feature.selectors';

@Injectable()
export class AddNewVehicleFeaturesEffects {
  getNewVehicleFeatures$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchVehicleFeature),
      concatLatestFrom(() => this.store.select(addVehicleFeatureQuery.selectCurrentVehicleInfo)),
      map(([, vehicleState]) =>
        fromAddVehicleFeaturesActions.getNewVehicleFeatures({
          payload: {
            vin: vehicleState.primaryVehicleInfo.vin,
            year: vehicleState.primaryVehicleInfo.year,
            typeOfVehicleCode: 'automobile'
          }
        })
      )
    )
  );

  buildPartnerCoverages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAddVehicleQuoteActions.addVehicleRateSuccess),
      concatLatestFrom(() => [
        this.store.select(userQuery.selectUserStateCode),
        this.store.select(addVehicleStateInfoQuery.selectHasTPMPackageAvailibility),
        this.store.select(addVehicleStateInfoQuery.selectHasCostcoGoldStarMembership)
      ]),
      map(([successAction, stateCode, hasTPMPackageAvailibility, hasCostcoGoldStarMembership]) =>
        buildPartnerCoverages({
          payload: successAction.payload,
          hasTPMPackageAvailibility,
          hasCostcoGoldStarMembership,
          copy: {
            coverageMap: this.copyService.getCopy('policy.vehicleCoverageCodeMapForPartners'),
            optOut: this.copyService.getCopy(
              'policy.vehicleCoverageCodesSupportedForOptOutForPartners'
            ),
            umpd: this.copyService.getCopy('policy.uninsuredMotoristPropertyDamage'),
            ers: this.copyService.getCopy('policy.paEmergencyRoadService'),
            hit: this.copyService.getCopy('policy.hit-and-RunPhantomVehicleDamage'),
            notAhit: this.copyService.getCopy('policy.notAHit-and-RunPhantomVehicleDamage')
          },
          stateCode
        })
      )
    )
  );

  initiateAddVehicle$ = createEffect(() =>
    // eslint-disable-next-line ngrx/prefer-effect-callback-in-block-statement
    this.actions$.pipe(
      ofType(addVehicleInitiate),
      concatLatestFrom(() => [
        this.store.select(addVehicleFeatureQuery.selectCurrentVehicleInfo),
        this.store.select(policyQuery.selectAdvanceAutoPolicies),
        this.store.select(BrandSelectors.selectIsPartner),
        this.store.select(vehicleQuery.selectNewVehicle)
      ]),
      map(([addVehicleInitiateAction, vehicleInfo, advancedAutoPolicies, partner, newVehicle]) => {
        let selectedPolicyAddress: { address: Address; policyNumber: string };
        if (advancedAutoPolicies.length > 1) {
          // When customer has multiple auto policies, check for matching garage location for each vehicle on each policy.
          // We are looking for the policy number that corresponds with the user's chosen garage address for the new vehicle
          selectedPolicyAddress = _flatMap(advancedAutoPolicies, advancedAutoPolicy => ({
            address: _get(
              advancedAutoPolicy.vehicles.find(
                vehicle =>
                  vehicle.garageLocationAddress.getStringifiedAddress() ===
                  vehicleInfo.garageAddress
              ),
              'garageLocationAddress'
            ),
            policyNumber: advancedAutoPolicy.policyNumber
          })).find(selectedObj => !!selectedObj.address);
        } else {
          selectedPolicyAddress = {
            address: advancedAutoPolicies[0].vehicles[0].garageLocationAddress,
            policyNumber: advancedAutoPolicies[0].policyNumber
          };
        }

        const initiateRequest: AddVehicleInitiateRequest = {
          policyNumber: selectedPolicyAddress.policyNumber,
          vehicle: {
            vin: vehicleInfo.primaryVehicleInfo.vin,
            year: vehicleInfo.primaryVehicleInfo.year,
            make: newVehicle.make,
            model: newVehicle.model,
            series: newVehicle.series,
            currentMileage: parseInt(vehicleInfo.primaryVehicleInfo.odoMeter, 10),
            annualMileage: parseInt(vehicleInfo.primaryUsageInfo.mileage, 10),
            ownershipDate: vehicleInfo.primaryUsageInfo.ownershipDateKey,
            msrp: String(newVehicle?.estimatedValue),
            primaryUsage: partner ? undefined : vehicleInfo.primaryUsageInfo.primaryUse,
            partnerPrimaryUse: partner ? vehicleInfo.primaryUsageInfo.primaryUse : undefined,
            garageAddress: {
              addressLine1: selectedPolicyAddress.address.addressLine1,
              city: selectedPolicyAddress.address.city,
              state: selectedPolicyAddress.address.state,
              postalCode: selectedPolicyAddress.address.zipCode,
              country: 'USA'
            },
            airBagsIndicator: vehicleInfo.vehicleFeatures.airBagsIndicator,
            antiLockBrakes: vehicleInfo.vehicleFeatures.antiLockBreaks,
            antiTheftDeviceIndicator: vehicleInfo.vehicleFeatures.antiTheftDeviceIndicator,
            antiTheftCategoryOne: vehicleInfo.vehicleFeatures.antiTheftCategoryOne,
            antiTheftCategoryTwo: vehicleInfo.vehicleFeatures.antiTheftCategoryTwo,
            antiTheftCategoryThree: vehicleInfo.vehicleFeatures.antiTheftCategoryThree,
            antiTheftCategoryFour: vehicleInfo.vehicleFeatures.antiTheftCategoryFour,
            dayTimeRunningLights: vehicleInfo.vehicleFeatures.dayTimeRunningLight,
            electronicStability: vehicleInfo.vehicleFeatures.electronicStabilityControl,
            hasOtherOrCoowner: vehicleInfo.underwritingQuestions.hasOtherOrCoowner,
            tpis: [],
            coverages: []
          },
          drivers: []
        };

        if (!partner) {
          delete initiateRequest.vehicle.primaryUsage;
          delete initiateRequest.vehicle.currentMileage;
        }

        return fromAddVehicleInfoActions.addVehicleInitiate({
          payload: {
            request: initiateRequest,
            hasTPI: addVehicleInitiateAction.hasTPI
          }
        });
      })
    )
  );

  rateAddVehicle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addVehicleRate, addVehicleResetRate),
      concatLatestFrom(() => [
        this.store.select(addVehicleFeatureQuery.selectVehicleRisks),
        this.store.select(addVehicleFeatureQuery.selectCurrentVehicleInfo),
        this.store.select(addVehicleStateInfoQuery.selectNewVehicleCoverages),
        this.store.select(addVehicleFeatureQuery.selectVehicleDriverAssignmentList),
        this.store.select(BrandSelectors.selectIsPartner),
        this.store.select(addVehicleStateInfoQuery.selectNewVehicleSubmissionId),
        this.store.select(BrandSelectors.selectPartnerId),
        this.store.select(vehicleQuery.selectNewVehicle)
      ]),
      map(
        ([
          rateAddVehicleAction,
          autoPoliciesRisks,
          vehicleInfo,
          vehicleCoveragesFromInitiate,
          driverAssignments,
          isPartner,
          submissionId,
          partnerId,
          newVehicle
        ]) => {
          for (const risk of autoPoliciesRisks) {
            if (risk.address.getStringifiedAddress() === vehicleInfo.garageAddress) {
              if (rateAddVehicleAction.type === addVehicleRate.type) {
                return fromAddVehicleQuoteActions.addVehicleRate(
                  this.addVehicleFeatureService.buildAddVehicleRatePayload(
                    risk,
                    vehicleInfo,
                    vehicleCoveragesFromInitiate,
                    driverAssignments,
                    false,
                    isPartner,
                    submissionId,
                    partnerId,
                    newVehicle
                  )
                );
              } else {
                return fromAddVehicleQuoteActions.addVehicleRate(
                  this.addVehicleFeatureService.buildAddVehicleRatePayload(
                    risk,
                    vehicleInfo,
                    vehicleCoveragesFromInitiate,
                    driverAssignments,
                    true,
                    isPartner,
                    submissionId,
                    partnerId,
                    newVehicle
                  )
                );
              }
            }
          }
        }
      )
    )
  );

  notificationAddVehicle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addVehicleSendNotification),
      map(action => action.payload),
      concatLatestFrom(() => [
        this.store.select(userQuery.selectUserStateCode),
        this.store.select(userQuery.selectUserCity),
        this.store.select(userQuery.selectFirstAndLastName),
        this.store.select(userQuery.selectCustomerId),
        this.store.select(userQuery.selectEmailAddress),
        this.store.select(userQuery.selectFirstName),
        this.store.select(userQuery.selectPartnerId),
        this.store.select(addVehicleFeatureQuery.selectCurrentVehicleInfo),
        this.store.select(vehicleQuery.selectNewRiskDescription),
        this.store.select(PolicySelectors.selectAdvanceAutoPolicies),
        this.store.select(vehicleFeatureQuery.selectNewVehiclesFeaturesDescriptionList),
        this.store.select(addVehicleQuoteQuery.selectNewVehicleRate),
        this.store.select(PolicySelectors.selectVehiclePolicies),
        this.store.select(vehicleQuery.selectNewVehicle)
      ]),
      switchMap(
        ([
          notificationPayload,
          userStateCode,
          userCity,
          customerName,
          customerId,
          customerEmailAddress,
          customerFirstName,
          partnerId,
          vehicleInfo,
          vehicleRiskDescription,
          autoPolicies,
          equipmentListFound,
          newVehicleRate,
          classicPolicies,
          newVehicle
        ]) => {
          const selectedPolicyNumber: string =
            autoPolicies.length === 0
              ? _get(
                  classicPolicies.find(
                    policy =>
                      _get(policy, 'assignedProducer.producerIdNum') ===
                      notificationPayload.agent.id
                  ),
                  'policyNumber',
                  ''
                )
              : _get(
                  autoPolicies.find(
                    policy =>
                      _get(policy, 'assignedProducer.producerIdNum') ===
                      notificationPayload.agent.id
                  ),
                  'policyNumber',
                  ''
                );

          const agentEmail = _get(
            notificationPayload,
            'agent.agencyEmails[0]',
            _get(notificationPayload, 'agent.emails[0]')
          );
          const newVehicleDiscountState = !!(<string[]>(
            this.copyService.getCopy('policy.newVehicleDiscountStates')
          )).find(state => state === userStateCode);

          const agentEmailNotificationPayload: AddVehicleNotificationRequestAgentEmail =
            Object.assign(
              {},
              {
                typeofEmail: _get(notificationPayload, 'typeOfEmail'),
                agentEmailAddress: agentEmail,
                agentName: _get(notificationPayload, 'agent.firstName'),
                hasAgencyEmail: !!_get(notificationPayload, 'agent.agencyEmails[0]'),
                customerName: customerName,
                policyNumber: selectedPolicyNumber,
                newVehicleDetails: vehicleRiskDescription,
                newVehicleVin: newVehicle?.vin || 'N/A',
                newVehicleMsrp: this.formatMSRP(newVehicle?.msrp || 'N/A'),
                estimatedVehiclePrice: this.formatMSRP(newVehicle?.estimatedValue || 'N/A'),
                odometerReading:
                  _get(vehicleInfo, 'primaryVehicleInfo.odoMeter') > 0
                    ? _get(vehicleInfo, 'primaryVehicleInfo.odoMeter')
                    : 'Not Provided',
                newVehicleDiscountActive: _has(vehicleInfo, 'isNewVehicleDiscountActive')
                  ? _get(vehicleInfo, 'isNewVehicleDiscountActive')
                    ? 'Yes'
                    : 'No'
                  : 'Not Provided',
                vehicleMainUse:
                  vehiclePrimaryUse[_get(vehicleInfo, 'primaryUsageInfo.primaryUse', '')] ||
                  'Not Provided',
                annualMileage: this.numberWithCommas(
                  _get(vehicleInfo, 'primaryUsageInfo.mileage', 'Not Provided')
                ),
                vehicleAddress: _get(vehicleInfo, 'garageAddress', 'Not Provided'),
                vehicleSecurityEquipmentFound:
                  equipmentListFound.length > 0 ? equipmentListFound.join(', ') : 'Not Provided',
                vehicleSecurityEquipmentEntered:
                  this.addVehicleFeatureService.featureEquipmentsListEntered(
                    equipmentListFound,
                    vehicleInfo.vehicleFeatures,
                    userStateCode
                  ),
                vehiclePrimaryDriver: this.addVehicleFeatureService.getPrimaryDriverOnVehicle(
                  _get(vehicleInfo, 'primaryVehicleInfo.vin'),
                  _get(vehicleInfo, 'driverAssignment')
                ),
                vehicleHasOwnerCoOwner: _get(vehicleInfo, 'underwritingQuestions.hasOtherOrCoowner')
                  ? 'Yes'
                  : 'No',
                vehicleUsedForBusiness: _get(vehicleInfo, 'underwritingQuestions.usedForBusiness')
                  ? 'Yes'
                  : 'No',
                vehicleKitCarReplia: _get(vehicleInfo, 'underwritingQuestions.kitCarReplica')
                  ? 'Yes'
                  : 'No',
                vehicleHasTPI:
                  _get(vehicleInfo, 'underwritingQuestions.hasLoanOrLease') ||
                  _get(vehicleInfo, 'underwritingQuestions.hasLoan') ||
                  _get(vehicleInfo, 'underwritingQuestions.hasLease')
                    ? 'Yes'
                    : 'No',
                vehicleUMPDSelected: vehicleInfo.vehicleCoverages
                  ? this.addVehicleFeatureService.getUMPDCoverageValue(
                      vehicleInfo.vehicleCoverages,
                      userStateCode
                    )
                  : vehicleCoverageSelectionOptions.NotApplicable,
                vehicleUIMPDSelected: vehicleInfo.vehicleCoverages
                  ? this.addVehicleFeatureService.getUIMPDCoverageValue(
                      vehicleInfo.vehicleCoverages,
                      userStateCode
                    )
                  : vehicleCoverageSelectionOptions.NotApplicable,
                vehicleFullGlassCoverageSelected: vehicleInfo.vehicleCoverages
                  ? this.addVehicleFeatureService.getFullGlassCoverageValue(
                      vehicleInfo.vehicleCoverages,
                      userStateCode
                    )
                  : vehicleCoverageSelectionOptions.NotApplicable,
                vehicleWorkOrderNumber: _get(vehicleInfo, 'workOrderNumber'),
                newVehicleDiscountDays: newVehicleDiscountState ? '30' : '14'
              }
            );

          const { classicOnlyAutoPolicyOrCompOnlyAdv, typeOfEmail } = notificationPayload;
          const ansPayload = this.addVehicleFeatureService.buildANSNotificationPayload(
            selectedPolicyNumber,
            this.addVehicleFeatureService.getANSText(
              classicOnlyAutoPolicyOrCompOnlyAdv
                ? ((typeOfEmail + 'ClassicOnlyAutoPolicyOrCompOnlyAdv') as typeOfAgentEmail)
                : typeOfEmail,
              agentEmailNotificationPayload
            ),
            notificationPayload.workOrderNumber,
            notificationPayload.agent,
            this.addVehicleFeatureService.getANSCode(
              notificationPayload.typeOfEmail,
              agentEmailNotificationPayload
            ),
            customerName
          );

          const policySubstring = selectedPolicyNumber.substring(5);

          const customerEmailPayload: TemplatedEmail =
            this.addVehicleFeatureService.buildTemplatedCustomerEmail(
              customerId,
              customerEmailAddress,
              customerFirstName,
              partnerId,
              notificationPayload.typeOfEmail,
              notificationPayload.agent,
              `XXXXX${policySubstring}`,
              vehicleRiskDescription,
              _get(newVehicleRate, 'totalCost'),
              _get(newVehicleRate, 'monthlyPremium'),
              _get(newVehicleRate, 'termMonths'),
              this.addVehicleFeatureService.getPrimaryDriversNames(vehicleInfo.driverAssignment),
              `${userCity}, ${userStateCode}`,
              this.addVehicleFeatureService.getCoveragesString(vehicleInfo.vehicleCoverages),
              this.addVehicleFeatureService.getFeesString(_get(newVehicleRate, 'fees')),
              this.addVehicleFeatureService.getDiscountsString(
                _get(newVehicleRate, 'appliedDiscounts')
              )
            );

          return [
            fromAddVehicleBindActions.sendAgentANSNotification({
              payload: ansPayload
            }),

            fromAddVehicleBindActions.sendAgentEmailNotification({
              payload: { ...agentEmailNotificationPayload, classicOnlyAutoPolicyOrCompOnlyAdv }
            }),

            fromAddVehicleBindActions.sendCustomerEmailNotification({
              payload: customerEmailPayload
            })
          ];
        }
      )
    )
  );

  partnerNotificationAddVehicle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addVehicleSendPartnerNotification),
      map(action => action.payload),
      concatLatestFrom(() => [
        this.store.select(userQuery.selectUserStateCode),
        this.store.select(userQuery.selectUserCity),
        this.store.select(userQuery.selectFirstAndLastName),
        this.store.select(userQuery.selectCustomerId),
        this.store.select(userQuery.selectEmailAddress),
        this.store.select(userQuery.selectFirstName),
        this.store.select(userQuery.selectPartnerId),
        this.store.select(BrandSelectors.selectAccountId),
        this.store.select(addVehicleFeatureQuery.selectCurrentVehicleInfo),
        this.store.select(vehicleQuery.selectNewRiskDescription),
        this.store.select(PolicySelectors.selectAdvanceAutoPolicies),
        this.store.select(vehicleFeatureQuery.selectNewVehiclesFeaturesDescriptionList),
        this.vehicleCoveragesFacade.rate$
      ]),
      switchMap(
        ([
          notificationPayload,
          userStateCode,
          userCity,
          customerName,
          customerId,
          customerEmailAddress,
          customerFirstName,
          partnerId,
          accountId,
          vehicleInfo,
          vehicleRiskDescription,
          autoPolicies,
          equipmentListFound,
          newVehicleRate
        ]) => {
          const partnerEmail = env.partnerLicensedAdvisorEmail;

          const newVehicleDiscountState = !!(<string[]>(
            this.copyService.getCopy('policy.newVehicleDiscountStates')
          )).find(state => state === userStateCode);

          const newVehicleUnder150MilesState = !!(<string[]>(
            this.copyService.getCopy('policy.newVehicleUnder150MilesStates')
          )).find(state => state === userStateCode);

          const partnerEmailNotificationPayload: AddVehicleNotificationRequestPartnerEmail =
            Object.assign(
              {},
              {
                typeofEmail: _get(notificationPayload, 'typeOfEmail'),
                partnerEmailAddress: partnerEmail,
                customerName: customerName,
                policyNumber: autoPolicies[0].policyNumber,
                newVehicleDetails: `${vehicleRiskDescription}`,
                newVehicleVin: `${vehicleInfo?.primaryVehicleInfo?.vin}`,
                odometerReading:
                  _get(vehicleInfo, 'primaryVehicleInfo.odoMeter') > 0
                    ? _get(vehicleInfo, 'primaryVehicleInfo.odoMeter')
                    : 'Not Provided',
                newVehicleDiscountActive: _has(vehicleInfo, 'isNewVehicleDiscountActive')
                  ? _get(vehicleInfo, 'isNewVehicleDiscountActive')
                    ? 'Yes'
                    : 'No'
                  : 'Not Provided',
                vehicleMainUse:
                  vehiclePrimaryUse[_get(vehicleInfo, 'primaryUsageInfo.primaryUse', '')] ||
                  vehiclePartnerUse[_get(vehicleInfo, 'primaryUsageInfo.primaryUse', '')] ||
                  vehicleCAPartnerUse[_get(vehicleInfo, 'primaryUsageInfo.primaryUse', '')] ||
                  'Not Provided',
                annualMileage: this.numberWithCommas(
                  _get(vehicleInfo, 'primaryUsageInfo.mileage', 'Not Provided')
                ),
                vehicleAddress: _get(vehicleInfo, 'garageAddress', 'Not Provided'),
                vehicleSecurityEquipmentFound:
                  equipmentListFound.length > 0 ? equipmentListFound.join(', ') : 'Not Provided',
                vehicleSecurityEquipmentEntered:
                  this.addVehicleFeatureService.featureEquipmentsListEntered(
                    equipmentListFound,
                    vehicleInfo.vehicleFeatures,
                    userStateCode
                  ),
                vehiclePrimaryDriver: this.addVehicleFeatureService.getPrimaryDriverOnVehicle(
                  _get(vehicleInfo, 'primaryVehicleInfo.vin'),
                  _get(vehicleInfo, 'driverAssignment')
                ),
                vehicleHasOwnerCoOwner: _get(vehicleInfo, 'underwritingQuestions.hasOtherOrCoowner')
                  ? 'Yes'
                  : 'No',
                vehicleUsedForBusiness: _get(vehicleInfo, 'underwritingQuestions.usedForBusiness')
                  ? 'Yes'
                  : 'No',
                vehicleKitCarReplia: _get(vehicleInfo, 'underwritingQuestions.kitCarReplica')
                  ? 'Yes'
                  : 'No',
                vehicleHasTPI:
                  _get(vehicleInfo, 'underwritingQuestions.hasLoanOrLease') ||
                  _get(vehicleInfo, 'underwritingQuestions.hasLoan') ||
                  _get(vehicleInfo, 'underwritingQuestions.hasLease')
                    ? 'Yes'
                    : 'No',
                vehicleUnrepairedDamage: _get(vehicleInfo, 'underwritingQuestions.isDamaged')
                  ? 'Yes'
                  : 'No',
                vehicleLeaseStartDate: _get(vehicleInfo, 'leaseStartDate'),
                vehicleUMPDSelected: vehicleInfo.vehicleCoverages
                  ? this.addVehicleFeatureService.getUMPDCoverageValue(
                      vehicleInfo.vehicleCoverages,
                      userStateCode
                    )
                  : vehicleCoverageSelectionOptions.NotProvided,
                vehicleUIMPDSelected: vehicleInfo.vehicleCoverages
                  ? this.addVehicleFeatureService.getUIMPDCoverageValue(
                      vehicleInfo.vehicleCoverages,
                      userStateCode
                    )
                  : vehicleCoverageSelectionOptions.NotProvided,
                vehicleFullGlassCoverageSelected: vehicleInfo.vehicleCoverages
                  ? this.addVehicleFeatureService.getFullGlassCoverageValue(
                      vehicleInfo.vehicleCoverages,
                      userStateCode
                    )
                  : vehicleCoverageSelectionOptions.NotProvided,
                vehicleWorkOrderNumber: _get(vehicleInfo, 'workOrderNumber'),
                newVehicleDiscountDays: newVehicleDiscountState ? '30' : '14',
                newVehicleUnderMileageLimit: newVehicleUnder150MilesState ? '150' : '500'
              }
            );
          const policySubstring = autoPolicies[0].policyNumber.substring(5);

          const customerEmailPayload: TemplatedEmail =
            this.addVehicleFeatureService.buildTemplatedPartnerCustomerEmail(
              customerId,
              customerEmailAddress,
              customerFirstName,
              partnerId,
              accountId,
              notificationPayload.typeOfEmail,
              `XXXXX${policySubstring}`,
              vehicleRiskDescription,
              _get(newVehicleRate, 'totalCost'),
              _get(newVehicleRate, 'monthlyPremium'),
              _get(newVehicleRate, 'termMonths'),
              this.addVehicleFeatureService.getPrimaryDriversNames(vehicleInfo.driverAssignment),
              `${userCity}, ${userStateCode}`,
              this.addVehicleFeatureService.getCoveragesString(vehicleInfo.vehicleCoverages),
              this.addVehicleFeatureService.getFeesString(_get(newVehicleRate, 'fees')),
              this.addVehicleFeatureService.getDiscountsString(
                _get(newVehicleRate, 'appliedDiscounts')
              )
            );
          return [
            fromAddVehicleBindActions.sendPartnerEmailNotification({
              payload: partnerEmailNotificationPayload
            }),

            fromAddVehicleBindActions.sendPartnerCustomerEmailNotification({
              payload: customerEmailPayload
            })
          ];
        }
      )
    )
  );

  notificationAddVehicleAfterBind$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addVehicleSendNotificationAfterBind),
      map(action => action.payload),
      concatLatestFrom(() => [
        this.store.select(userQuery.selectUserStateCode),
        this.store.select(userQuery.selectFirstAndLastName),
        this.store.select(userQuery.selectCustomerId),
        this.store.select(userQuery.selectEmailAddress),
        this.store.select(userQuery.selectFirstName),
        this.store.select(userQuery.selectPartnerId),
        this.store.select(addVehicleFeatureQuery.selectCurrentVehicleInfo),
        this.store.select(vehicleQuery.selectNewRiskDescription),
        this.store.select(vehicleQuery.selectNewVehicle),
        this.store.select(PolicySelectors.selectAdvanceAutoPolicies),
        this.store.select(vehicleFeatureQuery.selectNewVehiclesFeaturesDescriptionList),
        this.store.select(addVehicleQuoteQuery.selectNewVehicleRate),
        this.store.select(addVehicleFeatureQuery.selectFuturePayments),
        this.store.select(addVehicleQuoteQuery.selectNewVehicleCoverages),
        this.store.select(addVehicleFeatureQuery.selectCurrentAutoPolicy),
        this.store.select(addVehicleQuoteQuery.selectNewVehicleAppliedDiscounts),
        this.store.select(addVehicleInfoQuery.selectNumberOfVehicles)
      ]),
      switchMap(
        ([
          notificationPayload,
          userStateCode,
          customerName,
          customerId,
          customerEmailAddress,
          customerFirstName,
          partnerId,
          vehicleInfo,
          vehicleRiskDescription,
          newVehicle,
          autoPolicies,
          equipmentListFound,
          newVehicleRate,
          futurePayments,
          vehicleCoverages,
          currentPolicy,
          discounts,
          numberOfVehicles
        ]) => {
          const selectedPolicyNumber: string = _get(
            autoPolicies.find(
              policy =>
                _get(policy, 'assignedProducer.producerIdNum') === notificationPayload.agent.id
            ),
            'policyNumber',
            ''
          );

          const agentEmail = _get(
            notificationPayload,
            'agent.agencyEmails[0]',
            _get(notificationPayload, 'agent.emails[0]')
          );
          const newVehicleDiscountState = !!(<string[]>(
            this.copyService.getCopy('policy.newVehicleDiscountStates')
          )).find(state => state === userStateCode);

          const futurePaymentsMap =
            futurePayments && futurePayments.length > 0
              ? this.addVehicleFeatureService.transformKeyValuPairs(
                  'FuturePaymentSchedule',
                  this.vehicleQuoteService.mapPayments(
                    futurePayments.filter(
                      p => p?.estPaymentAmount > 0 && p.status === PaymentScheduleStatusType.PLANNED
                    )
                  )
                )
              : new Map();
          const vehicleCoveragesMap =
            vehicleCoverages && vehicleCoverages.length > 0
              ? this.addVehicleFeatureService.transformKeyValuPairs(
                  'VehicleCoverage',
                  this.addVehicleFeatureService.transformVehicleCoverages(vehicleCoverages)
                )
              : new Map();
          const policyCoveragesMap = currentPolicy
            ? this.addVehicleFeatureService.transformKeyValuPairs(
                'PolicyCoverage',
                this.addVehicleFeatureService.transformPolicyCoverages(currentPolicy)
              )
            : new Map();
          const discountsMap =
            discounts && discounts.length > 0
              ? this.addVehicleFeatureService.transformKeyValuPairs(
                  'Discounts',
                  this.addVehicleFeatureService.transformVehicleDiscounts(discounts),
                  true
                )
              : new Map();

          const agentEmailNotificationPayload: AddVehicleNotificationRequestAgentEmail =
            Object.assign(
              {},
              {
                typeofEmail: _get(notificationPayload, 'typeOfEmail'),
                agentEmailAddress: agentEmail,
                agentName: _get(notificationPayload, 'agent.firstName'),
                hasAgencyEmail: !!_get(notificationPayload, 'agent.agencyEmails[0]'),
                customerName: customerName,
                policyNumber: selectedPolicyNumber,
                newVehicleDetails: vehicleRiskDescription,
                newVehicleVin: vehicleInfo.primaryVehicleInfo.vin,
                newVehicleMsrp: this.formatMSRP(newVehicle?.msrp || 'N/A'),
                estimatedVehiclePrice: this.formatMSRP(newVehicle?.estimatedValue || 'N/A'),
                odometerReading:
                  _get(vehicleInfo, 'primaryVehicleInfo.odoMeter') > 0
                    ? _get(vehicleInfo, 'primaryVehicleInfo.odoMeter')
                    : 'Not Provided',
                newVehicleDiscountActive: _has(vehicleInfo, 'isNewVehicleDiscountActive')
                  ? _get(vehicleInfo, 'isNewVehicleDiscountActive')
                    ? 'Yes'
                    : 'No'
                  : 'Not Provided',
                vehicleMainUse:
                  vehiclePrimaryUse[_get(vehicleInfo, 'primaryUsageInfo.primaryUse', '')] ||
                  'Not Provided',
                annualMileage: this.numberWithCommas(
                  _get(vehicleInfo, 'primaryUsageInfo.mileage', 'Not Provided')
                ),
                vehicleAddress: _get(vehicleInfo, 'garageAddress', 'Not Provided'),
                vehicleSecurityEquipmentFound:
                  equipmentListFound.length > 0 ? equipmentListFound.join(', ') : 'Not Provided',
                vehicleSecurityEquipmentEntered:
                  this.addVehicleFeatureService.featureEquipmentsListEntered(
                    equipmentListFound,
                    vehicleInfo.vehicleFeatures,
                    userStateCode
                  ),
                vehiclePrimaryDriver: this.addVehicleFeatureService.getPrimaryDriverOnVehicle(
                  _get(vehicleInfo, 'primaryVehicleInfo.vin'),
                  _get(vehicleInfo, 'driverAssignment')
                ),
                vehicleHasOwnerCoOwner: _get(vehicleInfo, 'underwritingQuestions.hasOtherOrCoowner')
                  ? 'Yes'
                  : 'No',
                vehicleUsedForBusiness: _get(vehicleInfo, 'underwritingQuestions.usedForBusiness')
                  ? 'Yes'
                  : 'No',
                vehicleKitCarReplia: _get(vehicleInfo, 'underwritingQuestions.kitCarReplica')
                  ? 'Yes'
                  : 'No',
                vehicleHasTPI:
                  _get(vehicleInfo, 'underwritingQuestions.hasLoanOrLease') ||
                  _get(vehicleInfo, 'underwritingQuestions.hasLoan') ||
                  _get(vehicleInfo, 'underwritingQuestions.hasLease')
                    ? 'Yes'
                    : 'No',
                vehicleUMPDSelected: vehicleInfo.vehicleCoverages
                  ? this.addVehicleFeatureService.getUMPDCoverageValue(
                      vehicleInfo.vehicleCoverages,
                      userStateCode
                    )
                  : vehicleCoverageSelectionOptions.NotApplicable,
                vehicleUIMPDSelected: vehicleInfo.vehicleCoverages
                  ? this.addVehicleFeatureService.getUIMPDCoverageValue(
                      vehicleInfo.vehicleCoverages,
                      userStateCode
                    )
                  : vehicleCoverageSelectionOptions.NotApplicable,
                vehicleFullGlassCoverageSelected: vehicleInfo.vehicleCoverages
                  ? this.addVehicleFeatureService.getFullGlassCoverageValue(
                      vehicleInfo.vehicleCoverages,
                      userStateCode
                    )
                  : vehicleCoverageSelectionOptions.NotApplicable,
                vehicleWorkOrderNumber: _get(vehicleInfo, 'workOrderNumber'),
                newVehicleDiscountDays: newVehicleDiscountState ? '30' : '14'
              }
            );

          const ansPayload = this.addVehicleFeatureService.buildANSNotificationPayload(
            selectedPolicyNumber,
            this.addVehicleFeatureService.getANSText(
              notificationPayload.typeOfEmail,
              agentEmailNotificationPayload
            ),
            notificationPayload.workOrderNumber,
            notificationPayload.agent,
            this.addVehicleFeatureService.getANSCode(
              notificationPayload.typeOfEmail,
              agentEmailNotificationPayload
            ),
            customerName
          );

          const policySubstring = selectedPolicyNumber.substring(5);

          const customerEmailPayload: TemplatedEmail =
            this.addVehicleFeatureService.buildTemplatedCustomerEmailAfterBind(
              customerId,
              customerEmailAddress,
              customerFirstName,
              partnerId,
              notificationPayload.typeOfEmail,
              notificationPayload.agent,
              `XXXXX${policySubstring}`,
              vehicleRiskDescription,
              _get(newVehicleRate, 'totalCost'),
              _get(newVehicleRate, 'monthlyPremium'),
              _get(newVehicleRate, 'termMonths'),
              vehicleCoveragesMap,
              policyCoveragesMap,
              discountsMap,
              futurePaymentsMap,
              numberOfVehicles
            );

          return [
            fromAddVehicleBindActions.sendAgentANSNotification({
              payload: ansPayload
            }),

            fromAddVehicleBindActions.sendAgentEmailNotification({
              payload: agentEmailNotificationPayload
            }),

            fromAddVehicleBindActions.sendCustomerEmailNotification({
              payload: customerEmailPayload
            })
          ];
        }
      ),
      catchError(error => of(VehicleInfoActions.addVehicleSendNotificationAfterBindError(error)))
    )
  );

  // @todo - jjc054 : look into using the login in the partner version of this below to get the policyNumber as we know the current policy before binding
  bindAddNewVehicleQuote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addVehicleBindQuote),
      map(action => action.payload),
      concatLatestFrom(() => [
        this.store.select(PolicySelectors.selectAdvanceAutoPolicies),
        this.store.select(BrandSelectors.selectIsPartner)
      ]),
      switchMap(([addVehicleBindPayload, autoPolicies, isPartner]) => {
        const selectedPolicyNumber = _get(
          (<AutoPolicy[]>autoPolicies).find(
            policy =>
              _get(policy, 'assignedProducer.producerIdNum') === addVehicleBindPayload.agent.id
          ),
          'policyNumber',
          ''
        );
        return isPartner
          ? this.vehicleCoveragesFacade.getPartnerAddVehicleCoverageAction()
          : [
              fromAddVehicleBindActions.bindAddVehicleQuote({
                payload: {
                  workOrderNumber: addVehicleBindPayload.workOrderNumber,
                  policyNumber: selectedPolicyNumber,
                  vin: addVehicleBindPayload.vin
                }
              })
            ];
      })
    )
  );

  partnerBindAddNewVehicleQuote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addVehicleBindPartnerQuote),
      map(action => action.payload),
      concatLatestFrom(() => this.store.select(addVehicleFeatureQuery.selectCurrentAutoPolicy)),
      switchMap(([addVehicleBindPayload, autoPolicy]) => [
        fromAddVehicleBindActions.bindAddVehicleQuote({
          payload: {
            workOrderNumber: addVehicleBindPayload.workOrderNumber,
            policyNumber: autoPolicy.policyNumber
          }
        })
      ])
    )
  );

  dispatchBindQuoteNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        fromAddVehicleBindActions.bindAddVehicleQuoteSuccess,
        bindPartnerAddVehicleChangeCoverageQuoteSuccess
      ),
      concatLatestFrom(() => [
        this.store.select(addVehicleFeatureQuery.selectCurrentVehicleInfo),
        this.store.select(BrandSelectors.selectIsPartner)
      ]),
      map(([, vehicleInfo, isPartner]) => {
        if (isPartner) {
          return addVehicleSendPartnerNotification({
            payload: { typeOfEmail: vehicleInfo.typeOfAgentEmail }
          });
        } else {
          return addVehicleSendNotificationAfterBind({
            payload: { agent: vehicleInfo.selectedAgent, typeOfEmail: vehicleInfo.typeOfAgentEmail }
          });
        }
      })
    )
  );

  redirectToConfirmationPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAddVehicleBindActions.sendAgentANSNotificationSuccess),
      map(() =>
        fromRouterActions.Go({
          path: ['policies/add-vehicle/agent-notification-confirmation']
        })
      )
    )
  );

  redirectToPartnerConfirmationPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAddVehicleBindActions.sendPartnerEmailNotificationSuccess),
      map(() =>
        fromRouterActions.Go({
          path: ['policies/add-vehicle/agent-notification-confirmation']
        })
      )
    )
  );

  redirectToNewVehicleFeaturesPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAddVehicleFeaturesActions.getNewVehicleFeaturesSuccess),
      concatLatestFrom(() => [
        this.store.select(BrandSelectors.selectIsPartner),
        this.store.select(vehicleQuery.selectVehicleFeaturesCodesList)
      ]),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      switchMap(([action, isPartner, codeList]) => {
        const electronicStabilityControlCode = this.copyService.getCopy(
          'policy.newVehicleFeatureCodeElectronicStabilityControl'
        );
        const dayTimeRunningLightCode = this.copyService.getCopy(
          'policy.newVehicleFeatureCodeDaytimeRunningLights'
        );
        const abs4W = this.copyService.getCopy('policy.newVehicleFeatureCodeAntiLockBrakes4w');
        const absRear = this.copyService.getCopy('policy.newVehicleFeatureCodeAntiLockBrakesRear');
        const data: VehicleFeaturesInfoModel = {
          antiLockBreaks: codeList.some(code => [abs4W, absRear].includes(code)),
          dayTimeRunningLight: codeList.includes(dayTimeRunningLightCode),
          antiTheftDeviceIndicator: this.antiTheftDeviceIndicatorMap(
            codeList.find(code => code.indexOf('aud_alm') !== -1)
          ),
          electronicStabilityControl: codeList.includes(electronicStabilityControlCode),
          needAgent: false,
          kitCarOrReplica: false
        };
        const airBagsIndicator = codeList.find(code => !!this.airBagsIndicatorMap(code));
        if (airBagsIndicator) {
          Object.assign(data, { airBagsIndicator: this.airBagsIndicatorMap(airBagsIndicator) });
        }

        return [
          fromRouterActions.Go({
            path: ['policies/add-vehicle/underwriting-questions']
          }),
          VehicleInfoActions.setVehicleFeaturesInfo({
            featureInfo: data
          })
        ];
      })
    )
  );

  redirectAfterNewVehicleInitiate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAddVehicleInfoActions.addVehicleInitiateSuccess),
      map(payload => fromRouterActions.Go({ path: payload.nextRoutePath }))
    )
  );

  redirectOnFuturePaymentSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        fromAddVehicleQuoteActions.loadFuturePaymentsSuccess,
        fromAddVehicleQuoteActions.loadFuturePaymentsFailed
      ),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map((action: any) =>
        fromRouterActions.Go({
          path: [
            `policies/add-vehicle/${
              this.hasValidFuturePayments(action.payload?.paymentSchedule)
                ? 'vehicle-future-payments'
                : 'vehicle-quote-summary'
            }`
          ]
        })
      )
    )
  );

  redirectOnRateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAddVehicleQuoteActions.addVehicleRateSuccess, addVehicleCoverageForChangeCoverage),
      concatLatestFrom(() => [
        this.store.select(addVehicleFeatureQuery.selectCurrentVehicleInfo),
        this.store.select(userQuery.selectUserStateCode),
        this.store.select(BrandSelectors.selectIsPartner)
      ]),
      map(([, vehicleInfo, stateCode, isPartner]) => {
        const unsupportedStates = this.copyService.getCopy(
          'policy.partnerAddVehicleUnsupportedStates'
        );
        const quoteInfoPath = 'policies/add-vehicle/quote-info';
        const quoteSummaryPath = 'policies/add-vehicle/quote-summary';
        if (unsupportedStates.includes(stateCode.toLowerCase()) && isPartner) {
          return fromRouterActions.Go({ path: [quoteSummaryPath] });
        } else if (vehicleInfo.navigateToFuturePaymentSchedule) {
          if (isPartner) return fromRouterActions.Go({ path: [quoteSummaryPath] });
          else return fromAddVehicleQuoteActions.loadFuturePayments();
        } else {
          return fromRouterActions.Go({ path: [quoteInfoPath] });
        }
      })
    )
  );
  resetDataState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addVehicleReset),
      switchMap(() => [fromAddVehicleQuoteActions.addVehicleRateReset()])
    )
  );

  bindDataResetState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addVehicleBindReset),
      switchMap(() => [fromAddVehicleBindActions.bindAddVehicleQuoteReset()])
    )
  );

  /**
   * @author: Abhishek Singh
   * @returns:
   * @description: This effect triggers a analytics notification.
   */

  analyticsNotification$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(addVehicleSendAnalytics),
        map(action => action.payload),
        map(payload => this.addVehicleFeatureService.sendAddVehicleAnalytics(payload))
      ),
    { dispatch: false }
  );

  analyticsHardStopNotification$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(VehicleInfoActions.addVehicleHardStopSendAnalytics),
        map(action => action.payload),
        map(payload => this.addVehicleFeatureService.addVehicleHardStopSendAnalytics(payload))
      ),
    { dispatch: false }
  );

  expectationSettingNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addVehicleExpectationSettingNotification),
      concatLatestFrom(() => [
        this.store.select(userQuery.selectFirstAndLastName),
        this.store.select(userQuery.selectMyAccountEmail),
        this.store.select(userQuery.selectPrimaryEmail),
        this.store.select(BrandSelectors.selectPartnerId),
        this.store.select(vehicleQuery.selectNewVehicle),
        this.store.select(vehicleQuery.selectNewRiskDescription)
      ]),
      switchMap(
        ([
          action,
          customerName,
          accountEmail,
          primaryEmail,
          partnerId,
          vehicleVinInfo,
          vehicleRiskDescription
        ]) =>
          this.getAgentNotificationPayload(
            { ...action.payload },
            customerName,
            accountEmail,
            primaryEmail,
            partnerId,
            vehicleVinInfo?.vin,
            vehicleRiskDescription
          )
      )
    )
  );

  loadFuturePayments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadFuturePaymemts),
      map(() => fromAddVehicleQuoteActions.loadFuturePayments())
    )
  );

  // private getPolicyNumber(vehicle, autoPolicies: AutoPolicy[]) {
  //   const policy = autoPolicies.find(p =>
  //     p.vehicles.some(
  //       v => v.garageLocationAddress.getStringifiedAddress() === vehicle.garageAddress
  //     )
  //   );
  //   console.log(policy);
  //   return +policy.policyNumber;
  // }
  // Expectaton settings , section # 3
  private getAgentNotificationPayload(
    { agent, policyNumber },
    customerName,
    accountEmail,
    primaryEmail,
    partnerId,
    vin,
    vehicleRiskDesription
  ) {
    const notificationText = this.copyService.getCopy(Content.SEND_TO_AGENT_ANS, {
      agentName: agent.firstName,
      customerName,
      policyNumber: policyNumber,
      newVehicleDetails: vehicleRiskDesription || 'N/A',
      newVehicleVin: vin || 'N/A'
    });

    const ansPayload: ANSRequest = {
      notificationsRequest: {
        customerName,
        notificationText,
        notificationType: Code.SEND_TO_AGENT_ANS,
        createdBy: 'DMSI',
        policyNumber: policyNumber,
        referenceNumber: policyNumber,
        formattedReferenceNumber: new PrettyPolicyNum().transform(policyNumber),
        from: 'DMSI',
        dueDate: addDays(new Date(), 2).toISOString(),
        sourceSystem: 'MYACCOUNT',
        uniqueId: this.utilService.generateId()
      },
      producerId: agent.id
    };

    const agentEmail = _get(agent, 'agencyEmails[0]', _get(agent, 'emails[0]'));
    const agentEmailPayload: AddVehicleNotificationRequestAgentEmail = {
      agentName: agent.firstName,
      agentEmailAddress: agentEmail,
      customerName,
      policyNumber: policyNumber,
      newVehicleDetails: vehicleRiskDesription || 'N/A',
      newVehicleVin: vin || 'N/A',
      typeofEmail: typeOfAgentEmail.expectationSettingAgent
    };

    const recipientEmailAddress = primaryEmail || accountEmail;

    const messageDetails: MessageDetail[] = [];
    messageDetails.push({ key: 'EmailAddress', value: recipientEmailAddress.emailAddress });
    messageDetails.push({ key: 'SubscriberKey', value: recipientEmailAddress.emailAddress });
    messageDetails.push({ key: 'FirstName', value: customerName });
    messageDetails.push({ key: 'ProdID', value: agent.agentId });

    const customerEmailPayload = <TemplatedEmail>{
      templateCode: 'AddVehicleSentToAgent',
      brandId: 'MyAccount',
      language: 'EN',
      partnerId: partnerId,
      recipientAddress: recipientEmailAddress.emailAddress,
      messageDetails
    };

    return [
      fromAddVehicleBindActions.sendAgentANSNotification({
        payload: ansPayload
      }),
      fromAddVehicleBindActions.sendAgentEmailNotification({
        payload: agentEmailPayload
      }),
      fromAddVehicleBindActions.sendCustomerEmailNotification({
        payload: customerEmailPayload
      })
    ];
  }

  private airBagsIndicatorMap(code: string) {
    switch (code) {
      case 'fa_dr_fa_fp':
        return 'Both';
      case 'fa_dr':
        return 'front';
      case 'fa_fp':
        return 'other';
      default:
        return null;
    }
  }

  private antiTheftDeviceIndicatorMap(code: string) {
    switch (code) {
      case 'aud_alm_pas':
        return 'passive';
      case 'aud_alm_act':
        return 'active';
      case 'aud_alm_act_pas':
        return 'other';
      default:
        return 'none';
    }
  }

  private hasValidFuturePayments(paymentSchedule: PaymentScheduleItem[]): boolean {
    return (
      paymentSchedule &&
      paymentSchedule.filter(p => p.amount > 0 && p.status === 'planned')?.length > 0
    );
  }

  private formatMSRP(msrp: string) {
    const value = msrp.replace(/[^0-9.-]+/g, '');
    return this.currencyPipe.transform(value, 'USD', 'symbol', '1.0-2');
  }
  private numberWithCommas(odometerReading: string) {
    if (parseInt(odometerReading, 10) > 0) {
      const parts = odometerReading.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    }
  }
  // eslint-disable-next-line @typescript-eslint/member-ordering
  constructor(
    private actions$: Actions,
    private store: Store,
    private addVehicleFeatureService: AddVehicleFeatureService,
    private copyService: CopyService,
    private utilService: UtilService,
    private vehicleQuoteService: VehicleQuoteService,
    private vehicleCoveragesFacade: VehicleCoveragesFacade,
    private currencyPipe: CurrencyPipe
  ) {}
}
